import { NavLink } from 'react-router-dom';

import { mrdDepartmentConditionMaps, mrdDetailFieldMaps } from './mrd.mapping';

const genderLabel = (genders: string[]) => genders.includes('any') ? 'Any' : genders.map((gender) => gender.charAt(0).toUpperCase() + gender.slice(1)).join(', ');

const ageLabel = (minAgeYears: number | null, maxAgeYears: number | null) => {
  if (minAgeYears === null && maxAgeYears === null) return 'All ages';
  if (minAgeYears !== null && maxAgeYears !== null) return `${minAgeYears}-${maxAgeYears} years`;
  if (minAgeYears !== null) return `${minAgeYears}+ years`;
  return `Up to ${maxAgeYears} years`;
};

const patientRatioLabel = (oldRatio: number, newRatio: number) => `Old: ${oldRatio}% / New: ${newRatio}%`;

const genderRatioLabel = (maleRatio: number, femaleRatio: number) => `Male: ${maleRatio}% / Female: ${femaleRatio}%`;

const generationFlows = [
  {
    input: 'OPD Input',
    records: 'opd_registrations',
    doctor: 'All active department doctors, continuous round-robin',
    behavior: 'Creates doctor-specific ticket numbers and zero-value billing records.',
  },
  {
    input: 'IPD Admit',
    records: 'ipd_daycare_admissions + bed_occupancies',
    doctor: 'All active department doctors, continuous round-robin',
    behavior: 'Plans a weighted 1–10 day stay, creates its discharge summary, and assigns a bed with no overlapping occupancy interval.',
  },
  {
    input: 'EMG Input',
    records: 'emg_registrations + ipd_daycare_admissions (90%)',
    doctor: 'All active department doctors, continuous round-robin',
    behavior: 'Creates emergency registrations with zero-value billing records; 90% of the EMG patients are linked to real IPD admissions and beds.',
  },
  {
    input: 'OT Input',
    records: 'ot_cases',
    doctor: 'All active department doctors, continuous round-robin',
    behavior: '90% use same-date IPD patients. When the OT detail section is enabled, the Major/Minor totals determine the procedure type mix.',
  },
  {
    input: 'Pathology / Radiology detail counts',
    records: 'investigation_bills',
    doctor: 'All active department doctors, continuous round-robin',
    behavior: 'Each count creates an investigation using a random active test from its mapped charge category and a generated result.',
  },
] as const;

const generationRules = [
  { title: 'Old patients (31%)', value: 'patients', detail: 'Selected from active real patients matching the department age and gender conditions.' },
  { title: 'New patients (69%)', value: 'false_patients', detail: 'Synthetic patients stay outside the real patient master; least-recently-used eligible records are selected first.' },
  { title: 'Gender split', value: 'Male 68% / Female 32%', detail: 'Applied across each generation batch except female-only department mappings.' },
  { title: 'Synthetic patient use', value: 'last_update', detail: 'Updated to the use time whenever an existing false_patients record is selected.' },
  { title: 'IPD stay length', value: '1–10 days', detail: '1 day 5%; 2–6 days 55%; 7–8 days 25%; 9–10 days 15%. One-day stays are Daycare.' },
  { title: 'Discharge type', value: '90% / 9% / 1%', detail: 'Normal 90%, Referral 9%, and Death 1%; generated discharges are finalized automatically when due.' },
  { title: 'EMG to IPD', value: '90%', detail: '90% of generated EMG patients receive a linked IPD admission. These admissions consume beds and are included in the IPD Additional total.' },
  { title: 'IPD to OT', value: '90%', detail: '90% of generated OT cases reuse patients admitted to IPD in the same department on the selected date.' },
  { title: 'OT procedure mix', value: 'Major + Minor = OT', detail: 'The enabled OT detail totals must equal the complete OT count for the date; new cases fill the remaining Major/Minor totals.' },
  { title: 'Investigation details', value: 'Count = generated tests', detail: 'Every enabled Pathology or Radiology category count creates that many investigation bills from randomly selected mapped tests.' },
  { title: 'Doctor allocation', value: 'Round-robin', detail: 'Records continue rotating across every active doctor in the selected department, including later generation runs.' },
  { title: 'Bed capacity', value: 'Maximum 80%', detail: 'Maximum occupied beds = floor(live active beds × 0.80). Generation stops with a warning when no capacity remains.' },
  { title: 'Successful generation', value: 'Input → Additional', detail: 'Generated counts accumulate under Additional and the submitted Input/Admit values reset to zero.' },
  { title: 'Actual counts', value: 'Genuine records only', detail: 'MRD-generated records are tagged and excluded from Actual to prevent double counting.' },
] as const;

const patientCooldowns = [
  { module: 'OPD', period: '1 month', dateField: 'appointmentDate', behavior: 'A patient used in OPD cannot be selected for another generated OPD registration during this period.' },
  { module: 'IPD', period: '2 months', dateField: 'admissionDate', behavior: 'A patient used in IPD cannot be selected for another generated IPD admission during this period.' },
  { module: 'EMG', period: '3 months', dateField: 'registrationDate', behavior: 'A patient used in EMG cannot be selected for another generated EMG registration during this period.' },
] as const;

const generationPrerequisites = [
  'The department name must have a mapping below.',
  'At least one active doctor must belong to the department.',
  'Requested old-patient slots need age-, gender-, and cooldown-matched records in patients.',
  'IPD generation requires active department wards, beds, and an active referral.',
  'OT generation requires an active OT room, a procedure type, and at least one same-date IPD admission in the department; otherwise no OT record is created and a warning is shown.',
  'Every non-zero Pathology or Radiology count requires at least one active investigation test in its mapped charge category.',
  'When OT details are enabled, Major plus Minor must equal the complete OT total displayed in the MRD modal.',
  'Average occupancy and new IPD admissions must remain within 80% of live active beds.',
  'If any write fails, all records from that generation batch are rolled back.',
] as const;

export const MrdMappingPage = () => (
  <div className="page-stack mrd-page">
    <div className="page-heading">
      <div>
        <h1>MRD Generation Mapping & Rules</h1>
        <p>Complete reference for patient selection, doctor allocation, bed capacity, generated collections, and static IDs.</p>
      </div>
      <div className="page-heading-actions">
        <NavLink className="button button-secondary" to="/mrd">Back to MRD</NavLink>
      </div>
    </div>

    <section className="mrd-logic-grid" aria-label="MRD generation rules">
      {generationRules.map((rule) => (
        <article className="content-card mrd-logic-card" key={rule.title}>
          <span>{rule.title}</span>
          <strong>{rule.value}</strong>
          <p>{rule.detail}</p>
        </article>
      ))}
    </section>

    <section className="content-card mrd-map-card">
      <header>
        <div>
          <h2>Input → Generated Data</h2>
          <p>Where every MRD input is written and how its department doctors are assigned.</p>
        </div>
      </header>
      <div className="table-scroll">
        <table className="mrd-map-table">
          <thead>
            <tr><th>MRD Input</th><th>Generated Collection</th><th>Doctor Assignment</th><th>Special Behavior</th></tr>
          </thead>
          <tbody>
            {generationFlows.map((flow) => (
              <tr key={flow.input}>
                <td><strong>{flow.input}</strong></td>
                <td><code>{flow.records}</code></td>
                <td>{flow.doctor}</td>
                <td>{flow.behavior}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>

    <section className="content-card mrd-map-card mrd-rule-card">
      <header>
        <div>
          <h2>Validation & Required Setup</h2>
          <p>Generation is allowed only after every applicable rule passes.</p>
        </div>
      </header>
      <ol className="mrd-rule-list">
        {generationPrerequisites.map((rule, index) => (
          <li key={rule}><span>{index + 1}</span><p>{rule}</p></li>
        ))}
      </ol>
    </section>

    <section className="content-card mrd-map-card">
      <header>
        <div>
          <h2>Patient Reuse Cooldown</h2>
          <p>The cooldown applies to both patients and false_patients. Recent module usage is checked before selection.</p>
        </div>
      </header>
      <div className="table-scroll">
        <table className="mrd-map-table">
          <thead>
            <tr><th>Module</th><th>Reuse Block</th><th>Date Checked</th><th>Rule</th></tr>
          </thead>
          <tbody>
            {patientCooldowns.map((rule) => (
              <tr key={rule.module}>
                <td><strong>{rule.module}</strong></td>
                <td><span className="mrd-cooldown-badge">{rule.period}</span></td>
                <td><code>{rule.dateField}</code></td>
                <td>{rule.behavior}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>

    <section className="content-card mrd-map-card">
      <header>
        <div>
          <h2>Department Patient Conditions</h2>
          <p>Age, gender, patient-source ratio, and gender ratio sent to the generator. Ratios remain fixed for every generation run.</p>
        </div>
      </header>
      <div className="table-scroll">
        <table className="mrd-map-table">
          <thead>
            <tr><th>Department</th><th>Gender</th><th>Age Condition</th><th>Patient Ratio</th><th>Gender Ratio</th><th>Reference Beds</th><th>Department ID</th></tr>
          </thead>
          <tbody>
            {mrdDepartmentConditionMaps.map((entry) => (
              <tr key={entry.departmentId}>
                <td>{entry.department}</td>
                <td>{genderLabel(entry.genders)}</td>
                <td>{ageLabel(entry.minAgeYears, entry.maxAgeYears)}</td>
                <td>{patientRatioLabel(entry.oldPatientRatio, entry.newPatientRatio)}</td>
                <td>{genderRatioLabel(entry.malePatientRatio, entry.femalePatientRatio)}</td>
                <td>{entry.bedCount}</td>
                <td>{entry.departmentId}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>

    <section className="content-card mrd-map-card">
      <header>
        <div>
          <h2>Detail Fields</h2>
          <p>MRD modal field name to charge_categories collection _id. These counts are saved and generate random active tests from the mapped category.</p>
        </div>
      </header>
      <div className="table-scroll">
        <table className="mrd-map-table">
          <thead>
            <tr><th>Field</th><th>Section</th><th>Collection</th><th>Mapped ID</th></tr>
          </thead>
          <tbody>
            {mrdDetailFieldMaps.map((entry) => (
              <tr key={entry.key}>
                <td>{entry.label}</td>
                <td>{entry.section}</td>
                <td>{entry.collection}</td>
                <td className={entry.chargeCategoryId ? '' : 'missing'}>{entry.chargeCategoryId || 'Not mapped'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  </div>
);
